import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Package, Tag, FileText, LogIn, Shield, LayoutDashboard, Mail, ChevronRight, Award, BookOpen, FolderDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { CartBadge } from './CartBadge';
import UserDropdown from './UserDropdown';

/**
 * Site-wide top nav. Mounts above every route so customers can always:
 *   - jump Home / Browse Products / Open the Pricing Catalog
 *   - hit My Orders (authed) or Admin (admin role)
 *   - see live cart status via the CartBadge chip
 *
 * iter114s.45 — The "Resume draft" amber chip + its session-storage
 * polling were retired. Drafts are still surfaced inside the customer
 * dashboard (and via the CartBadge for in-progress orders), so the
 * top nav no longer needs a parallel affordance.
 */
const NAV_LINKS = [
  { to: '/splash', label: 'Home', icon: Home, end: true },
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, authed: true, testId: 'nav-dashboard', featured: true },
  { to: '/products', label: 'Products', icon: Package },
  { to: '/sales-resources', label: 'Sales Resources', icon: FolderDown, testId: 'nav-sales-resources' },
  { to: '/pricing-catalog', label: 'Pricing', icon: Tag, testId: 'nav-pricing-catalog' },
  { to: '/blog', label: 'Blog', icon: BookOpen, testId: 'nav-blog' },
  { to: '/po-history', label: 'My Orders', icon: FileText, authed: true },
];

export const TopNav = () => {
  const location = useLocation();
  const { isAuthenticated, isAdmin, user, isEvergreenTier } = useAuth();
  const { items: breadcrumbItems } = useBreadcrumbs();

  // Hide on the login/register pages for focus
  if (location.pathname === '/login' || location.pathname === '/register') return null;

  const isActive = (link) => {
    if (link.end) return location.pathname === link.to || location.pathname === '/';
    return location.pathname.startsWith(link.to);
  };

  return (
    <nav
      className="sticky top-0 z-40 bg-[#E8F1E6] text-[#14281D] border-b border-[#6FBF73]/40 shadow-sm"
      data-testid="top-nav"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center h-20 gap-2">
        <Link
          to="/splash"
          className="flex items-center gap-3 mr-6 font-bold tracking-tight"
          data-testid="nav-logo"
        >
          <img
            src="/eg3-logo.png"
            alt="Evergreen Graphics Group"
            className="h-14 w-14 rounded-full ring-1 ring-[#6FBF73]/30"
            style={{ objectFit: 'contain' }}
          />
          <span className="hidden sm:inline text-sm text-[#14281D]">Evergreen Graphics</span>
        </Link>

        {/* iter114e: inline Strategic Partner badge — replaces the
            full-row StrategicPartnerBanner that previously sat below
            the TopNav. Only shown to evergreen-tier accounts so end-
            users don't see "Strategic Partner" branding that doesn't
            apply to them. Award icon + amber-on-emerald pill matches
            the previous banner's accent palette so the visual cue
            survives the row collapse. Hidden below md to preserve
            scroll space on mobile (the original banner also wrapped
            awkwardly on phones). */}
        {isAuthenticated && isEvergreenTier && (
          <div
            className="hidden md:flex items-center gap-1.5 mr-3 px-3 py-1 rounded-full bg-gradient-to-r from-[#2F5D3A] via-[#4A7C59] to-[#2F5D3A] text-white text-xs font-medium shadow-inner border border-[#6FBF73]/40"
            data-testid="strategic-partner-pill"
            title="A Strategic Partner in the Promotional Products Industry"
          >
            <Award className="w-3.5 h-3.5 text-amber-300 shrink-0" />
            <span className="whitespace-nowrap">Strategic Partner</span>
          </div>
        )}

        <div className="flex items-center gap-1 sm:gap-2 flex-1 overflow-x-auto">
          {NAV_LINKS.filter((l) => !l.authed || isAuthenticated).map((link) => {
            const Icon = link.icon;
            const active = isActive(link);
            // Featured links (Dashboard) get a persistent amber ring when
            // NOT on the current page so they draw the eye as the primary
            // post-login destination.
            const featuredIdle = link.featured && !active;
            return (
              <Link
                key={link.to}
                to={link.to}
                data-testid={link.testId || `nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-2 px-2.5 sm:px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                  active
                    ? 'bg-[#6FBF73] text-white font-semibold shadow-sm'
                    : featuredIdle
                      ? 'bg-white text-[#2F5D3A] ring-1 ring-[#6FBF73]/60 hover:bg-[#6FBF73] hover:text-white font-semibold'
                      : 'text-[#14281D] hover:bg-white/70'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden md:inline">{link.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Active draft cart — green pill with item count + total. Click
            jumps straight to /order-summary so customers can review +
            keep adding without hunting for the cart. Hidden when there's
            no in-progress draft. */}
        <CartBadge routeKey={location.pathname} isAuthenticated={isAuthenticated} />

        {/* iter114s.45 — "Resume draft" chip removed at operator's
            request. The CartBadge above still surfaces in-progress
            orders, and the customer dashboard exposes saved drafts
            for return visits. */}

        {/* Account Manager chip — only for Evergreen users with a rep assigned */}
        {isAuthenticated && user?.account_manager?.email && (
          <a
            href={`mailto:${user.account_manager.email}`}
            data-testid="nav-account-manager"
            className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white hover:bg-[#6FBF73]/10 text-[#2F5D3A] text-xs font-semibold shrink-0 border border-[#6FBF73]/40"
            title={`Contact your Account Manager: ${user.account_manager.name}`}
          >
            <div className="w-5 h-5 rounded-full bg-[#6FBF73] text-white flex items-center justify-center text-[10px] font-bold" aria-hidden="true">
              {user.account_manager.name?.[0]?.toUpperCase() || 'A'}
            </div>
            <span className="sr-only">Account manager:</span>
            <span className="hidden lg:inline">Your rep:</span>
            <span>{user.account_manager.name}</span>
            <Mail className="w-3 h-3 opacity-70" />
          </a>
        )}

        <div className="flex items-center gap-2 shrink-0 ml-2">
          {isAdmin && (
            <Link
              to="/admin"
              data-testid="nav-link-admin"
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm bg-purple-600 hover:bg-purple-700 text-white font-medium shadow-sm"
            >
              <Shield className="w-4 h-4" />
              <span className="hidden md:inline">Admin</span>
            </Link>
          )}
          {isAuthenticated ? (
            // iter114b: unified `<UserDropdown />` everywhere — replaces
            // the slim "👤 JASON | logout" two-button cluster that was
            // duplicated next to the same dropdown shown on
            // CategoryPage / Products / ProductDetail / QuoteHeader.
            // Same component everywhere = same affordance, no
            // duplicate "JASON" label.
            <UserDropdown />
          ) : (
            <Link
              to="/login"
              data-testid="nav-link-login"
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm bg-[#2F5D3A] hover:bg-[#14281D] text-white font-semibold shadow-sm"
            >
              <LogIn className="w-4 h-4" />
              <span className="hidden sm:inline">Log In</span>
            </Link>
          )}
        </div>
      </div>

      {/* Breadcrumb sub-strip — iter114s.44 — operator asked to switch
          this row from the dark `#1F3D2C` chrome to the lighter EG3
          brand green `#6FBF73` so the trail reads as a deliberate
          accent band instead of "more dark nav". Text re-coloured to
          `#14281D` on the green for AAA contrast; current-item gets
          a white pill for emphasis; chevrons use a darker green. */}
      {breadcrumbItems.length > 0 && (
        <div
          className="bg-[#6FBF73] border-t border-[#4A7C59]"
          data-testid="nav-breadcrumbs"
        >
          <div className="max-w-7xl mx-auto px-4 h-9 flex items-center gap-1 text-xs text-[#14281D] overflow-x-auto">
            <Link
              to="/splash"
              className="inline-flex items-center gap-1 text-[#14281D] hover:text-white transition-colors font-medium"
              data-testid="breadcrumb-home"
            >
              <Home className="w-3.5 h-3.5" />
              <span>Home</span>
            </Link>
            {breadcrumbItems.map((item, idx) => {
              const isLast = idx === breadcrumbItems.length - 1;
              return (
                <React.Fragment key={`${item.label}-${idx}`}>
                  <ChevronRight className="w-3.5 h-3.5 text-[#2F5D3A]" />
                  {isLast || !item.to ? (
                    <span
                      className="font-bold text-white"
                      aria-current={isLast ? 'page' : undefined}
                      data-testid="breadcrumb-current"
                    >
                      {item.label}
                    </span>
                  ) : (
                    <Link
                      to={item.to}
                      className="text-[#14281D] hover:text-white transition-colors font-medium"
                      data-testid={`breadcrumb-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.label}
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
};
